import type { Message } from "@mazzager/contracts";

const DATABASE = "mazzager";
const VERSION = 1;

export interface OutboxItem {
  clientMessageId: string;
  conversationId: string;
  optimistic: Message;
  request: {
    clientMessageId: string;
    kind: Message["kind"];
    text: string;
    attachmentIds: string[];
  };
}

function openDatabase(): Promise<IDBDatabase | null> {
  if (typeof indexedDB === "undefined") return Promise.resolve(null);
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DATABASE, VERSION);
    request.onupgradeneeded = () => {
      const database = request.result;
      if (!database.objectStoreNames.contains("messageCache")) database.createObjectStore("messageCache", { keyPath: "conversationId" });
      if (!database.objectStoreNames.contains("outbox")) database.createObjectStore("outbox", { keyPath: "clientMessageId" });
    };
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
}

async function read<T>(storeName: string, key?: IDBValidKey): Promise<T | undefined> {
  const database = await openDatabase();
  if (!database) return undefined;
  return new Promise((resolve, reject) => {
    const transaction = database.transaction(storeName, "readonly");
    const request = key === undefined ? transaction.objectStore(storeName).getAll() : transaction.objectStore(storeName).get(key);
    request.onsuccess = () => resolve(request.result as T | undefined);
    request.onerror = () => reject(request.error);
    transaction.oncomplete = () => database.close();
  });
}

async function write(storeName: string, value?: unknown, removeKey?: IDBValidKey): Promise<void> {
  const database = await openDatabase();
  if (!database) return;
  return new Promise((resolve, reject) => {
    const transaction = database.transaction(storeName, "readwrite");
    if (removeKey !== undefined) transaction.objectStore(storeName).delete(removeKey);
    else transaction.objectStore(storeName).put(value);
    transaction.oncomplete = () => { database.close(); resolve(); };
    transaction.onerror = () => { database.close(); reject(transaction.error); };
  });
}

export async function cachedMessages(conversationId: string): Promise<Message[]> {
  const row = await read<{ conversationId: string; messages: Message[] }>("messageCache", conversationId);
  return row?.messages ?? [];
}

export function cacheMessages(conversationId: string, messages: Message[]): Promise<void> {
  return write("messageCache", { conversationId, messages });
}

export function enqueueOutbox(item: OutboxItem): Promise<void> {
  return write("outbox", item);
}

export async function outboxItems(): Promise<OutboxItem[]> {
  return (await read<OutboxItem[]>("outbox")) ?? [];
}

export function removeOutbox(clientMessageId: string): Promise<void> {
  return write("outbox", undefined, clientMessageId);
}
